import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { useAuth } from '../stores/auth';
import { ApiRequestError } from '../lib/api';
import { AuthShell, AuthSubmit, AuthNote } from '../components/AuthShell';
import { Field as SiteField, Input as SiteInput } from '../components/site/Form';
import { INTERIORS } from '../lib/site';
import { Button, Input } from '../components/ui';

const MIN = 10;

/** 0–5, the prototype's `pwScore` with the platform's 10-character floor: length first, then variety. */
export function passwordScore(pw: string): number {
  let n = 0;
  if (pw.length >= MIN) n++;
  if (pw.length >= 14) n++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) n++;
  if (/\d/.test(pw)) n++;
  if (/[^A-Za-z0-9]/.test(pw)) n++;
  return pw.length < MIN ? Math.min(n, 1) : Math.min(n, 5);
}
const LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];

export function PasswordField({ label, value, onChange, autoComplete, hint, site }: { label: string; value: string; onChange: (v: string) => void; autoComplete: string; hint?: string; /** The public site's controls (the forced change). */ site?: boolean }) {
  const [peek, setPeek] = useState(false);
  const toggle = (
    <button type="button" onClick={() => setPeek((p) => !p)} className={`absolute top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-900 ${site ? 'right-2 grid h-10 w-10 place-items-center rounded-full' : 'right-1 rounded-md p-1.5'}`} title={peek ? 'Hide' : 'Show'} tabIndex={-1}>
      {peek ? <EyeOff className={site ? 'h-4 w-4' : 'h-3.5 w-3.5'} /> : <Eye className={site ? 'h-4 w-4' : 'h-3.5 w-3.5'} />}
    </button>
  );
  if (site) {
    return (
      <SiteField label={label} hint={hint}>
        <span className="relative block">
          <SiteInput type={peek ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} className="pr-14" />
          {toggle}
        </span>
      </SiteField>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-ink-500">{label}</span>
      <span className="relative block">
        <Input type={peek ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} className="w-full pr-9" />
        {toggle}
      </span>
      {hint && <span className="mt-1 block text-[11px] text-ink-500">{hint}</span>}
    </label>
  );
}

/**
 * Change your password (user menu), or — straight after an administrator's reset — choose one before anything else
 * (prototype §14 `screenChangePw`, forced). The server enforces the rules; the meter is a guide.
 */
export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.mustChangePassword;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const score = passwordScore(next);
  const local = user?.email?.split('@')[0]?.toLowerCase() ?? '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!forced && !current) return setError('Enter your current password.');
    if (next.length < MIN) return setError(`Use at least ${MIN} characters.`);
    if (local.length >= 4 && next.toLowerCase().includes(local)) return setError('Do not use your email address.');
    if (next !== confirm) return setError('The two new passwords do not match.');
    setBusy(true);
    try {
      await changePassword(forced ? null : current, next);
      setDone(true);
      setCurrent(''); setNext(''); setConfirm('');
      if (forced) navigate('/app', { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not change the password');
    } finally { setBusy(false); }
  };

  const form = (
    <form onSubmit={submit} className={forced ? 'space-y-5' : 'space-y-3.5'}>
      {!forced && <PasswordField label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" />}
      <div>
        <PasswordField site={forced} label="New password" value={next} onChange={setNext} autoComplete="new-password" hint={`At least ${MIN} characters. Longer is better than complicated.`} />
        <div className="mt-2 flex items-center gap-3" aria-live="polite">
          <div className="flex h-1.5 flex-1 gap-1" data-testid="pw-meter">
            {[1, 2, 3, 4, 5].map((i) => (
              <span key={i} className={`h-full flex-1 rounded-full ${i <= score ? (score >= 4 ? 'bg-emerald-500' : score >= 3 ? 'bg-amber-400' : 'bg-red-400') : 'bg-ink-200'}`} />
            ))}
          </div>
          <span className={`w-20 text-right font-medium text-ink-500 ${forced ? 'text-[12.5px]' : 'text-[11px]'}`}>{next ? LABELS[score] : ''}</span>
        </div>
      </div>
      <PasswordField site={forced} label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
      {error && (forced ? <AuthNote>{error}</AuthNote> : <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{error}</p>)}
      {done && !forced && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-700">Password changed. Your other devices have been signed out.</p>}
      {forced
        ? <div className="pt-1"><AuthSubmit disabled={busy}>{busy ? 'Saving…' : 'Save new password'}</AuthSubmit></div>
        : <Button type="submit" disabled={busy} className="w-full">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save new password</Button>}
    </form>
  );

  if (forced) {
    return (
      <AuthShell image={INTERIORS.livingArea} title={<>Choose a <em>new password</em></>} subtitle="Pick one only you know before continuing."
        below={<button type="button" onClick={async () => { await logout(); navigate('/login'); }} className="copy-link font-medium">Sign out instead</button>}>
        {form}
      </AuthShell>
    );
  }
  return (
    <div className="mx-auto max-w-[420px] pt-2">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-bronze-50 text-bronze-600"><KeyRound className="h-4 w-4" /></div>
        <div>
          <h1 className="font-display text-[17px] font-semibold tracking-[-0.01em] text-ink-900">Change your password</h1>
          <p className="text-[12px] text-ink-500">Signed in as {user?.email}.</p>
        </div>
      </div>
      <div className="surface p-6">{form}</div>
    </div>
  );
}
